if (!customElements.get('quick-cart-drawer')) {
  class QuickCartDrawer extends HTMLElement {
    constructor() {
      super();

      this.productCache = new Map();
      this.maxCacheEntries = 20;
      this.autoPreloadCount = 0;
      this.autoPreloadLimit = 14;
      this.toggleState = false;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handlePreloadEvent = this.handlePreloadEvent.bind(this);
      this.schedulePreloadScan = this.schedulePreloadScan.bind(this);

      if (window.Shopify && Shopify.designMode && this.parentElement) {
        window.addEventListener('shopify:section:load', this.init.bind(this));
        this.parentElement.addEventListener('shopify:section:select', () => this.open());
        this.parentElement.addEventListener('shopify:section:deselect', () => this.close());
      }
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('pointerover', this.handlePreloadEvent);
      document.removeEventListener('focusin', this.handlePreloadEvent);
      document.removeEventListener('touchstart', this.handlePreloadEvent);

      if (this._quickCartPreloadObserver) {
        this._quickCartPreloadObserver.disconnect();
      }

      if (this._triggersObserver) {
        this._triggersObserver.disconnect();
      }
    }

    init() {
      this.injectPatchStyles();

      if (!this.classList.contains('is--open')) {
        document.body.classList.remove('overflow-hidden');
      }

      if (!this._coreListenersInit) {
        this.initCoreListeners();
        this._coreListenersInit = true;
      }

      this.initTriggers();
    }

    initCoreListeners() {
      const closeButton = this.querySelector('.button--close');
      const backdrop = this.querySelector('.quick-cart-drawer__backdrop');
      const blocks = this.querySelector('.quick-cart-drawer__blocks');

      if (closeButton) {
        closeButton.addEventListener('click', this.close.bind(this));
      }

      if (backdrop) {
        backdrop.addEventListener('click', this.close.bind(this));
      }

      if (!blocks) return;

      blocks.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.toggleState) {
          this.close();
        }

        if (!this.toggleState || event.key !== 'Tab') return;

        const focusableElements = this.querySelectorAll(
          'button, [href], input, select, label, textarea, [tabindex]:not([tabindex="-1"])'
        );

        const firstFocusableElement = focusableElements[0];
        const lastFocusableElement = focusableElements[focusableElements.length - 1];

        if (!firstFocusableElement || !lastFocusableElement) return;

        if (event.shiftKey && document.activeElement === firstFocusableElement) {
          event.preventDefault();
          lastFocusableElement.focus();
        }

        if (!event.shiftKey && document.activeElement === lastFocusableElement) {
          event.preventDefault();
          firstFocusableElement.focus();
        }
      });
    }

    injectPatchStyles() {
      if (document.getElementById('dn-quick-cart-drawer-patch-styles')) return;

      const style = document.createElement('style');
      style.id = 'dn-quick-cart-drawer-patch-styles';
      style.textContent = `
        .dn-quickcart-swiper-wrap {
          position: relative;
        }

        .dn-quickcart-arrow {
          position: absolute;
          top: 50%;
          z-index: 20;
          width: 38px;
          height: 38px;
          border-radius: 999px;
          border: 1px solid rgba(130, 135, 145, 0.28);
          background: rgba(245, 247, 250, 0.72);
          color: #111;
          display: none;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transform: translateY(-50%);
          backdrop-filter: blur(12px) saturate(140%);
          -webkit-backdrop-filter: blur(12px) saturate(140%);
        }

        .dn-quickcart-arrow--prev {
          left: 12px;
        }

        .dn-quickcart-arrow--next {
          right: 12px;
        }

        .dn-quickcart-arrow svg {
          width: 18px;
          height: 18px;
        }

        @media (min-width: 991px) {
          .dn-quickcart-arrow {
            display: flex;
          }

          .quick-cart-drawer__media-swiper {
            cursor: default;
          }
        }
      `;

      document.head.appendChild(style);
    }

    initTriggers() {
      if (!this._triggerListenersInit) {
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('pointerover', this.handlePreloadEvent);
        document.addEventListener('focusin', this.handlePreloadEvent);
        document.addEventListener('touchstart', this.handlePreloadEvent, { passive: true });

        this._triggerListenersInit = true;
      }

      if ('IntersectionObserver' in window && !this._quickCartPreloadObserver) {
        this._quickCartPreloadObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;

            this.preloadTrigger(entry.target, true);
            this._quickCartPreloadObserver.unobserve(entry.target);
          });
        }, {
          rootMargin: '500px 0px'
        });
      }

      this.scanPreloadTargets();

      if (!this._triggersObserver && document.body) {
        this._triggersObserver = new MutationObserver(this.schedulePreloadScan);
        this._triggersObserver.observe(document.body, {
          childList: true,
          subtree: true
        });
      }
    }

    schedulePreloadScan() {
      if (this._preloadScanScheduled) return;

      this._preloadScanScheduled = true;

      window.requestAnimationFrame(() => {
        this._preloadScanScheduled = false;
        this.scanPreloadTargets();
      });
    }

    scanPreloadTargets() {
      if (!this._quickCartPreloadObserver) return;

      document.querySelectorAll('.quick-cart-drawer__trigger[data-product-url]').forEach(trigger => {
        if (trigger._dnQuickCartObserved) return;

        trigger._dnQuickCartObserved = true;
        this._quickCartPreloadObserver.observe(trigger);
      });
    }

    getTriggerFromEvent(event) {
      const target = event.target instanceof Element ? event.target : null;

      if (!target) return null;

      return target.closest('.quick-cart-drawer__trigger[data-product-url]');
    }

    handleDocumentClick(event) {
      const trigger = this.getTriggerFromEvent(event);

      if (!trigger) return;

      this.fetchProductForQuickCartDrawer(event);
    }

    handlePreloadEvent(event) {
      const trigger = this.getTriggerFromEvent(event);

      if (!trigger) return;

      this.preloadTrigger(trigger);
    }

    escapeAttribute(value) {
      return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    }

    getQuickCartUrl(trigger) {
      let fetchPrefix = '';
      const routesRoot = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
      const locale = window.Shopify ? Shopify.locale : '';

      if (locale && routesRoot.includes(`/${locale}/`)) {
        fetchPrefix = `/${locale}`;
      }

      return `${fetchPrefix}${trigger.dataset.productUrl}/?view=quick-cart`;
    }

    storeProductRequest(url, request) {
      if (!this.productCache.has(url) && this.productCache.size >= this.maxCacheEntries) {
        const oldestKey = this.productCache.keys().next().value;

        if (oldestKey) {
          this.productCache.delete(oldestKey);
        }
      }

      this.productCache.set(url, request);
    }

    preloadTrigger(trigger, automatic = false) {
      if (!trigger || !trigger.dataset.productUrl) return;

      const url = this.getQuickCartUrl(trigger);

      if (this.productCache.has(url)) return;

      if (automatic) {
        if (this.autoPreloadCount >= this.autoPreloadLimit) return;
        this.autoPreloadCount += 1;
      }

      const request = fetch(url, { credentials: 'same-origin' })
        .then(response => {
          if (!response.ok) throw new Error('Quick cart fetch failed');
          return response.text();
        })
        .catch(() => {
          this.productCache.delete(url);
          return '';
        });

      this.storeProductRequest(url, request);
    }

    async fetchQuickCartHtml(trigger) {
      const url = this.getQuickCartUrl(trigger);

      if (!this.productCache.has(url)) {
        this.preloadTrigger(trigger);
      }

      const request = this.productCache.get(url);

      if (!request) return '';

      return await request;
    }

    async fetchProductForQuickCartDrawer(event) {
      event.preventDefault();

      const target = event.target instanceof Element ? event.target : null;
      const trigger = this.getTriggerFromEvent(event);

      if (!trigger || trigger._quickCartLoading) return;

      this.activeTrigger = trigger;
      this.pauseCardSlider(target);

      const isRecommendations = trigger.classList.contains('quick-cart-drawer__trigger--recommendations');
      const sourceProductCard = trigger.closest('product-card');
      let productOptions = null;

      if (!isRecommendations) {
        if (!sourceProductCard) return;
        productOptions = sourceProductCard.querySelectorAll('input[type="radio"]:checked, select');
      }

      const wasPreloaded = this.productCache.has(this.getQuickCartUrl(trigger));

      trigger._quickCartLoading = true;

      if (!wasPreloaded) {
        trigger.classList.add('is--loading');
      }

      try {
        const productCardHTML = await this.fetchQuickCartHtml(trigger);

        if (!productCardHTML) return;

        const htmlContainer = document.createElement('div');
        htmlContainer.insertAdjacentHTML('beforeend', productCardHTML);

        const quickCartProduct = htmlContainer.querySelector('.quick-cart-product');
        const quickCartMain = this.querySelector('.quick-cart-drawer__main');

        if (!quickCartProduct || !quickCartMain) return;

        quickCartMain.innerHTML = '';
        quickCartMain.append(quickCartProduct);

        this.initMediaSlider();

        if (!isRecommendations && productOptions) {
          this.syncOptionsFromProductCard(productOptions, sourceProductCard);
        }

        const drawerProductCard = this.querySelector('product-card');

        if (drawerProductCard && drawerProductCard.init) {
          drawerProductCard.init();
        }

        this.bindVariantLabelUpdates();
        this.open();
      } catch (error) {
        this.productCache.delete(this.getQuickCartUrl(trigger));
      } finally {
        trigger._quickCartLoading = false;
        trigger.classList.remove('is--loading');
      }
    }

    pauseCardSlider(target) {
      const cardSlider = target ? target.closest('card-product-slider') : null;

      if (cardSlider && cardSlider.slider && cardSlider.slider.autoplay) {
        cardSlider.slider.autoplay.stop();
        cardSlider.classList.add('product--open-on-quick-cart');
      }
    }

    initMediaSlider() {
      if (this.sliderInstance && this.sliderInstance.destroy) {
        try {
          this.sliderInstance.destroy(true, true);
        } catch (error) {}
      }

      const swiperEl = this.querySelector('.quick-cart-drawer__media-swiper');

      if (!swiperEl) return;

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const wrapper = swiperEl.querySelector('.swiper-wrapper');

      this.resetMediaSliderStyles(swiperEl, wrapper);

      if (isMobile) {
        this.initMobileMediaScroll(swiperEl, wrapper);
        return;
      }

      if (typeof Swiper === 'undefined') return;

      this.sliderInstance = new Swiper(swiperEl, {
        slidesPerView: 2,
        spaceBetween: 8,
        allowTouchMove: false,
        simulateTouch: false,
        grabCursor: false,
        watchOverflow: true,
        freeMode: {
          enabled: false
        }
      });

      this.addDesktopArrows(swiperEl);
    }

    resetMediaSliderStyles(swiperEl, wrapper) {
      swiperEl.removeAttribute('data-native-scroll');
      swiperEl.classList.remove('swiper-initialized', 'swiper-horizontal', 'swiper-backface-hidden');

      swiperEl.style.overflowX = '';
      swiperEl.style.overflowY = '';
      swiperEl.style.webkitOverflowScrolling = '';
      swiperEl.style.touchAction = '';
      swiperEl.style.transform = '';
      swiperEl.style.paddingBottom = '';
      swiperEl.style.scrollSnapType = '';
      swiperEl.style.scrollBehavior = '';

      if (wrapper) {
        wrapper.style.display = '';
        wrapper.style.flexDirection = '';
        wrapper.style.width = '';
        wrapper.style.transform = '';
        wrapper.style.gap = '';
      }

      swiperEl.querySelectorAll('.swiper-slide').forEach(slide => {
        slide.style.flex = '';
        slide.style.width = '';
        slide.style.maxWidth = '';
        slide.style.scrollSnapAlign = '';
        slide.style.scrollSnapStop = '';
        slide.style.touchAction = '';
      });
    }

    initMobileMediaScroll(swiperEl, wrapper) {
      swiperEl.style.overflowX = 'auto';
      swiperEl.style.overflowY = 'hidden';
      swiperEl.style.webkitOverflowScrolling = 'touch';
      swiperEl.style.touchAction = 'auto';
      swiperEl.style.scrollSnapType = 'x mandatory';
      swiperEl.style.scrollBehavior = 'smooth';
      swiperEl.style.paddingBottom = '0';

      if (wrapper) {
        wrapper.style.display = 'flex';
        wrapper.style.flexDirection = 'row';
        wrapper.style.width = 'auto';
        wrapper.style.transform = 'none';
        wrapper.style.gap = '0';
      }

      swiperEl.querySelectorAll('.swiper-slide').forEach(slide => {
        slide.style.flex = '0 0 100%';
        slide.style.width = '100%';
        slide.style.maxWidth = '100%';
        slide.style.scrollSnapAlign = 'start';
        slide.style.scrollSnapStop = 'always';
        slide.style.touchAction = 'auto';
      });
    }

    addDesktopArrows(swiperEl) {
      if (!swiperEl || swiperEl.querySelectorAll('.swiper-slide').length < 2) return;

      const wrap = swiperEl.parentElement;

      if (!wrap) return;

      wrap.classList.add('dn-quickcart-swiper-wrap');

      if (wrap.querySelector('.dn-quickcart-arrow')) return;

      const prev = this.makeArrow('prev');
      const next = this.makeArrow('next');

      prev.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();

        if (this.sliderInstance && this.sliderInstance.slidePrev) {
          this.sliderInstance.slidePrev();
        }
      });

      next.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();

        if (this.sliderInstance && this.sliderInstance.slideNext) {
          this.sliderInstance.slideNext();
        }
      });

      wrap.appendChild(prev);
      wrap.appendChild(next);
    }

    makeArrow(direction) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `dn-quickcart-arrow dn-quickcart-arrow--${direction}`;
      button.setAttribute('aria-label', direction === 'prev' ? 'Previous image' : 'Next image');

      button.innerHTML = direction === 'prev'
        ? '<svg viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        : '<svg viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      return button;
    }

    syncOptionsFromProductCard(productOptions, sourceProductCard) {
      productOptions.forEach(productOption => {
        let productOptionName = productOption.name;
        const optionSectionIdAttribute = productOption.dataset ? productOption.dataset.sectionId : '';

        if (optionSectionIdAttribute) {
          productOptionName = productOptionName.replace(optionSectionIdAttribute, '');
        }

        const drawerOptionName = `${productOptionName}-quick-cart-product`;
        const quickCartDrawerOption = this.querySelector(
          `[name="${this.escapeAttribute(drawerOptionName)}"][value="${this.escapeAttribute(productOption.value)}"]`
        );

        if (!quickCartDrawerOption) return;

        const fieldset = quickCartDrawerOption.closest('fieldset');
        const quickCartDrawerOptionLabel = fieldset ? fieldset.querySelector('legend') : null;

        this.querySelectorAll(`[name="${this.escapeAttribute(drawerOptionName)}"]`).forEach(radioInput => {
          radioInput.checked = false;
          radioInput.removeAttribute('checked');
          radioInput.closest('li')?.classList.remove('checked');
        });

        quickCartDrawerOption.checked = true;
        quickCartDrawerOption.setAttribute('checked', '');
        quickCartDrawerOption.closest('li')?.classList.add('checked');

        const quickCartDrawerOptionLabelSpan = quickCartDrawerOptionLabel?.querySelector('[data-selected-variant]');

        if (quickCartDrawerOptionLabelSpan) {
          quickCartDrawerOptionLabelSpan.innerHTML = productOption.value;
        }

        const drawerProductCard = this.querySelector('product-card');
        const drawerIdInput = drawerProductCard?.querySelector('input[name="id"]');
        const sourceIdInput = sourceProductCard?.querySelector('input[name="id"]');

        if (drawerIdInput && sourceIdInput) {
          drawerIdInput.value = sourceIdInput.value;
        }

        quickCartDrawerOption.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    bindVariantLabelUpdates() {
      const quickCartMain = this.querySelector('.quick-cart-drawer__main');

      if (!quickCartMain) return;

      quickCartMain.querySelectorAll('.variant-option-radio-input').forEach(radioInput => {
        if (radioInput._quickCartLabelBound) return;

        radioInput.addEventListener('change', event => {
          const fieldset = event.target.closest('fieldset');
          const quickCartDrawerOptionLabel = fieldset ? fieldset.querySelector('legend') : null;
          const quickCartDrawerOptionLabelSpan = quickCartDrawerOptionLabel?.querySelector('[data-selected-variant]');

          if (quickCartDrawerOptionLabelSpan) {
            quickCartDrawerOptionLabelSpan.innerHTML = event.target.value;
          }

          const selectedVariantId =
            this.querySelector(".product-card__add-to-cart--form input[name='id']")?.value ||
            this.querySelector("input[name='id']")?.value;

          const productVariantsData = this.querySelector('product-card')?.variantsObj || [];
          const featuredMedia = productVariantsData.find(variant => variant.id == selectedVariantId)?.featured_media;

          if (featuredMedia) {
            this.setActiveMedia(featuredMedia.id);
          }
        });

        radioInput._quickCartLabelBound = true;
      });
    }

    setActiveMedia(id) {
      const mediaFound = Array.from(this.querySelectorAll('[data-media-id]')).find(media => Number(media.dataset.mediaId) === id);

      if (mediaFound && this.sliderInstance) {
        this.sliderInstance.slideTo(Number(mediaFound.dataset.index));
      }
    }

    toggle() {
      if (!this.toggleState) {
        this.open();
      } else {
        this.close();
      }
    }

    open() {
      this.toggleState = true;
      document.body.classList.add('overflow-hidden');

      const quickCartDrawer = this.querySelector('.quick-cart-drawer__blocks');
      const closeButton = quickCartDrawer ? quickCartDrawer.querySelector('.button--close') : null;

      if (quickCartDrawer) {
        quickCartDrawer.setAttribute('tabindex', '0');
      }

      if (closeButton) {
        closeButton.setAttribute('tabindex', '0');
        closeButton.setAttribute('aria-expanded', true);
      }

      if (this.activeTrigger) {
        this.activeTrigger.setAttribute('aria-expanded', true);
      }

      this.classList.add('is--open');
      this.opened();

      const firstFocusableElement = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }

    close() {
      this.toggleState = false;

      const shopTheLookDrawer = document.querySelector('shop-the-look-drawer');

      if (shopTheLookDrawer && !shopTheLookDrawer.classList.contains('is--open')) {
        document.body.classList.remove('overflow-hidden');
      }

      if (!shopTheLookDrawer) {
        document.body.classList.remove('overflow-hidden');
      }

      this.classList.remove('is--open');
      this.closed();
      this.toggleAriaExpanded();

      this.activeTrigger = null;

      const openSlider = document.querySelector('card-product-slider.product--open-on-quick-cart');

      if (openSlider && openSlider.slider && openSlider.slider.autoplay) {
        openSlider.slider.autoplay.start();
        openSlider.classList.remove('product--open-on-quick-cart');
      }
    }

    toggleAriaExpanded(event) {
      if (event) {
        const button = event.target.closest('button');

        if (button) {
          button.setAttribute('aria-expanded', true);
        }

        this.querySelector('.button--close')?.setAttribute('aria-expanded', true);
      } else {
        document.querySelectorAll('[aria-controls="quick-cart-drawer"]').forEach(button => {
          button.setAttribute('aria-expanded', false);
        });
      }
    }

    opened() {
      const openedEvent = new Event('opened', { bubbles: true });
      this.dispatchEvent(openedEvent);
    }

    closed() {
      const closedEvent = new Event('closed', { bubbles: true });
      this.dispatchEvent(closedEvent);
    }
  }

  customElements.define('quick-cart-drawer', QuickCartDrawer);
}
